import { useRef, useState, useEffect, useCallback } from 'react';
import { Bold, Italic, Underline, List, ListOrdered } from 'lucide-react';
import { serializeEditor, textLength } from './richText';
import styles from './RichText.module.css';

const TOOLS = [
  { command: 'bold',                label: 'Bold',          Icon: Bold },
  { command: 'italic',              label: 'Italic',        Icon: Italic },
  { command: 'underline',           label: 'Underline',     Icon: Underline },
  { command: 'insertUnorderedList', label: 'Bulleted list', Icon: List },
  { command: 'insertOrderedList',   label: 'Numbered list', Icon: ListOrdered },
];

// Not every environment has the editing commands (jsdom has none); the editor
// still types, it just can't format.
const exec = (command, value) =>
  typeof document.execCommand === 'function' && document.execCommand(command, false, value);
const queryState = (command) => {
  try { return typeof document.queryCommandState === 'function' && document.queryCommandState(command); }
  catch { return false; }
};

/**
 * A small rich text field: bold, italic, underline and the two list kinds,
 * over a contentEditable. Uncontrolled — it reports the document (see
 * richText.js) through `onChange` on every edit, and starts empty each time it
 * mounts.
 *
 * `maxLength` is held here rather than just reported: typing and pasting past
 * it are cut off at the limit, the same as a textarea's maxLength. Paste comes
 * in as plain text, so formatting from elsewhere can't sneak in.
 *
 * `renderToolbar(toolbar)` lets the caller place the toolbar — beside its label,
 * say. Without it the toolbar sits directly above the field.
 */
function RichTextEditor({ id, maxLength, placeholder, onChange, ariaLabelledBy, renderToolbar }) {
  const ref = useRef(null);
  const [length, setLength] = useState(0);
  const [empty, setEmpty]   = useState(true);
  const [active, setActive] = useState({});

  const report = useCallback(() => {
    const blocks = serializeEditor(ref.current);
    const n = textLength(blocks);
    setLength(n);
    // A list with nothing typed in it yet still hides the placeholder.
    setEmpty(n === 0 && !ref.current.querySelector('li'));
    onChange?.(blocks);
  }, [onChange]);

  const refreshActive = useCallback(() => {
    const sel = document.getSelection?.();
    if (!sel || !ref.current || !ref.current.contains(sel.anchorNode)) return;
    setActive(Object.fromEntries(TOOLS.map(t => [t.command, queryState(t.command)])));
  }, []);

  useEffect(() => {
    document.addEventListener('selectionchange', refreshActive);
    return () => document.removeEventListener('selectionchange', refreshActive);
  }, [refreshActive]);

  /* Characters the current selection would free up if replaced. */
  const selectedLength = () => {
    const sel = document.getSelection?.();
    if (!sel || !sel.rangeCount || !ref.current.contains(sel.anchorNode)) return 0;
    return sel.toString().replace(/\n/g, '').length;
  };

  const room = () => (maxLength == null ? Infinity : maxLength - length + selectedLength());

  /* Native beforeinput, so typing past the limit is refused before it lands.
     A line break adds nothing to the count, so Enter is always allowed. */
  useEffect(() => {
    const el = ref.current;
    const onBeforeInput = (e) => {
      if (maxLength == null || !e.inputType?.startsWith('insert')) return;
      if (e.inputType === 'insertParagraph' || e.inputType === 'insertLineBreak'
        || e.inputType === 'insertOrderedList' || e.inputType === 'insertUnorderedList') return;
      const text = e.data ?? e.dataTransfer?.getData('text/plain') ?? '';
      const left = room();
      if (text.length <= left) return;
      e.preventDefault();
      if (left > 0 && e.inputType === 'insertText') exec('insertText', text.slice(0, left));
    };
    el.addEventListener('beforeinput', onBeforeInput);
    return () => el.removeEventListener('beforeinput', onBeforeInput);
  });

  function handlePaste(e) {
    e.preventDefault();
    const text = (e.clipboardData?.getData('text/plain') || '').replace(/\r\n?/g, '\n');
    const fits = text.slice(0, Math.max(0, room()));
    if (fits) exec('insertText', fits);
  }

  function handleFocus() {
    // Tags rather than inline styles, which is what the serializer reads best.
    exec('styleWithCSS', false);
  }

  function apply(command) {
    ref.current.focus();
    exec(command);
    report();
    refreshActive();
  }

  const toolbar = (
    <div className={styles.toolbar} role="toolbar" aria-label="Formatting">
      {TOOLS.map(({ command, label, Icon }) => (
        <button
          key={command}
          type="button"
          className={`${styles.tool} ${active[command] ? styles.toolActive : ''}`}
          aria-label={label}
          aria-pressed={!!active[command]}
          title={label}
          // mousedown, not click: a click would move focus out of the field and
          // lose the selection the command is meant to act on.
          onMouseDown={e => { e.preventDefault(); apply(command); }}
        >
          <Icon size={16} strokeWidth={1.75} />
        </button>
      ))}
    </div>
  );

  return (
    <>
      {renderToolbar ? renderToolbar(toolbar) : toolbar}
      <div className={styles.field}>
        {empty && <span className={styles.placeholder} aria-hidden="true">{placeholder}</span>}
        <div
          id={id}
          ref={ref}
          className={styles.editable}
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-labelledby={ariaLabelledBy}
          aria-placeholder={placeholder}
          onInput={report}
          onPaste={handlePaste}
          onDrop={e => e.preventDefault()}
          onFocus={handleFocus}
          onKeyUp={refreshActive}
          onMouseUp={refreshActive}
        />
        {maxLength != null && <span className={styles.count}>{length}/{maxLength}</span>}
      </div>
    </>
  );
}

export default RichTextEditor;
